import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ArrowLeft, BarChart3, Activity, Loader2, AlertTriangle, CheckCircle2, Clock, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';

function SummaryCard({ title, value, subtitle, icon: Icon, color = '#1E4E8C' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`summary-card-${title.toLowerCase().replace(/\s/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

function SCurveTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div className="rounded-lg p-3 shadow-xl text-xs" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <p className="font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span style={{ color: 'var(--text-muted)' }}>{entry.name}:</span>
          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{(entry.value || 0).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

export default function EngineeringAnalytics() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isDark } = useTheme();
  const activeProjectId = projectId || '69ce1f51cf186fdef37ef4a6';

  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState('weekly');
  const [sCurveData, setSCurveData] = useState(null);
  const [summary, setSummary] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [scurveRes, summaryRes] = await Promise.all([
        api.get(`/projects/${activeProjectId}/engineering-schedule/summary/s-curve?interval=${interval}`),
        api.get(`/projects/${activeProjectId}/engineering-schedule/summary`),
      ]);
      setSCurveData(scurveRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally { setLoading(false); }
  }, [activeProjectId, interval]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData = (sCurveData?.data_points || []).map((point) => {
    const date = new Date(point.date);
    return { date: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), planned: point.planned_cumulative || 0, forecast: point.forecast_cumulative || 0, actual: point.actual_cumulative || 0 };
  });

  const disciplineData = (summary?.by_discipline || []).map((d) => ({
    discipline: d.discipline, total: d.total_documents, completed: d.completed_documents, overdue: d.overdue_documents, progress: d.average_progress,
  }));

  const docTypeData = (summary?.by_document_type || []).map((dt) => ({
    name: dt.document_type_code, fullName: dt.document_type_name, total: dt.total_documents, completed: dt.completed_documents, overdue: dt.overdue_documents, progress: dt.average_progress,
  }));

  const gridColor = isDark ? '#1e293b' : '#e2e8f0';
  const tickColor = isDark ? '#64748b' : '#94a3b8';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[600px]">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent)' }} />
        </div>
      </DashboardLayout>
    );
  }

  const totalDocs = summary?.total_documents || 0;
  const completedDocs = (summary?.by_document_type || []).reduce((s, dt) => s + (dt.completed_documents || 0), 0);
  const overdueDocs = (summary?.by_document_type || []).reduce((s, dt) => s + (dt.overdue_documents || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls/engineering')}
          style={{ color: 'var(--text-muted)' }} data-testid="back-to-engineering-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Engineering Schedule
        </Button>

        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#143A6B]/20 to-[#1E4E8C]/10" style={{ border: '1px solid rgba(139,92,246,0.3)' }}>
            <BarChart3 className="w-8 h-8 text-[#143A6B]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Engineering Analytics</h1>
            <p style={{ color: 'var(--text-muted)' }}>S-Curve progress tracking and discipline breakdown</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryCard title="Total Documents" value={totalDocs} icon={FileSpreadsheet} color="#1E4E8C" />
          <SummaryCard title="Completed" value={completedDocs} subtitle={totalDocs > 0 ? `${((completedDocs / totalDocs) * 100).toFixed(0)}%` : '0%'} icon={CheckCircle2} color="#10B981" />
          <SummaryCard title="Overdue" value={overdueDocs} subtitle={overdueDocs > 0 ? 'Needs attention' : 'On track'} icon={AlertTriangle} color="#EF4444" />
          <SummaryCard title="Planned Progress" value={`${(summary?.overall_planned_progress || 0).toFixed(1)}%`} icon={Clock} color="#6366F1" />
          <SummaryCard title="Actual Progress" value={`${(summary?.overall_actual_progress || 0).toFixed(1)}%`}
            subtitle={`Variance: ${((summary?.overall_actual_progress || 0) - (summary?.overall_planned_progress || 0)).toFixed(1)}%`} icon={Activity} color="#143A6B" />
        </div>

        {/* S-Curve */}
        <div className="rounded-xl p-6" data-testid="s-curve-chart-container"
          style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>S-Curve Progress</h3>
            <div className="flex gap-2">
              {['weekly', 'monthly'].map((iv) => (
                <button key={iv} onClick={() => setInterval(iv)} data-testid={`interval-${iv}`}
                  className="px-3 py-1 text-xs rounded-md transition-colors"
                  style={{
                    backgroundColor: interval === iv ? 'var(--accent)' : 'var(--card-bg-alt)',
                    color: interval === iv ? '#fff' : 'var(--text-muted)',
                    border: interval === iv ? 'none' : '1px solid var(--border-color)',
                  }}>
                  {iv.charAt(0).toUpperCase() + iv.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="planGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1E4E8C" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1E4E8C" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="date" stroke={gridColor} tick={{ fill: tickColor, fontSize: 11 }} />
                <YAxis stroke={gridColor} tick={{ fill: tickColor, fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<SCurveTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px', color: tickColor }} iconType="plainline" />
                <Area type="monotone" dataKey="planned" name="Plan" stroke="#1E4E8C" strokeWidth={2} fill="url(#planGradient)" dot={false} />
                <Area type="monotone" dataKey="actual" name="Actual" stroke="#10B981" strokeWidth={2} fill="url(#actualGradient)" dot={false} />
                <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#F97316" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px]" style={{ color: 'var(--text-muted)' }}>
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No S-Curve data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Discipline Breakdown */}
        {disciplineData.length > 0 && (
          <div className="rounded-xl p-6" data-testid="discipline-chart-container"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Progress by Discipline</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={disciplineData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis type="number" stroke={gridColor} tick={{ fill: tickColor, fontSize: 11 }} domain={[0, 'dataMax']} />
                <YAxis type="category" dataKey="discipline" stroke={gridColor} tick={{ fill: tickColor, fontSize: 12 }} width={120} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[0, 4, 4, 0]} />
                <Bar dataKey="overdue" name="Overdue" fill="#EF4444" radius={[0, 4, 4, 0]} />
                <Bar dataKey="total" name="Total" fill="#1E4E8C" radius={[0, 4, 4, 0]} opacity={0.4} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Doc Type Table */}
        {docTypeData.length > 0 && (
          <div className="rounded-xl overflow-hidden" data-testid="doc-type-summary-table"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Progress by Document Type</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: 'var(--card-bg-alt)' }}>
                    {['Type', 'Name', 'Total', 'Completed', 'Overdue', 'Progress'].map((h) => (
                      <th key={h} className={`p-3 font-medium ${h === 'Total' || h === 'Completed' || h === 'Overdue' ? 'text-center' : 'text-left'}`}
                        style={{ color: 'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {docTypeData.map((dt) => (
                    <tr key={dt.name} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td className="p-3">
                        <span className="px-2 py-0.5 text-xs rounded font-semibold"
                          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid rgba(56,96,255,0.15)' }}>{dt.name}</span>
                      </td>
                      <td className="p-3" style={{ color: 'var(--text-secondary)' }}>{dt.fullName}</td>
                      <td className="p-3 text-center" style={{ color: 'var(--text-secondary)' }}>{dt.total}</td>
                      <td className="p-3 text-center" style={{ color: 'var(--status-completed-text)' }}>{dt.completed}</td>
                      <td className="p-3 text-center" style={{ color: 'var(--status-delayed-text)' }}>{dt.overdue}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.min(dt.progress, 100)}%`, backgroundColor: 'var(--accent)' }} />
                          </div>
                          <span className="text-xs min-w-[32px] text-right" style={{ color: 'var(--text-muted)' }}>{(dt.progress || 0).toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
